import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api/client';
import { useApi } from '@/lib/api/useApi';
import { useUserContext } from '@/lib/auth/useUserContext';
import { formatShortDate } from '@/lib/format';

type Audience = 'all' | 'managers' | 'baristas' | 'shift_leads';

interface Announcement {
  id: string;
  title: string;
  body: string;
  pinned: boolean;
  audience: Audience;
  authorId: string;
  expiresAt: string | null;
  createdAt: string;
  targetStoreIds: string[];
  mediaUrl: string | null;
  mediaType: string | null;
}

interface StoreLookup {
  id: string;
  code: string;
  name: string;
}

export function Announcements() {
  const { tier, scopedStores } = useUserContext();
  const [rows, setRows] = useState<Announcement[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    apiFetch<{ announcements: Announcement[] }>('/announcements')
      .then((r) => setRows(r.announcements))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <div className="min-h-screen p-6 max-w-3xl mx-auto">
      <header className="flex items-center justify-between border-b border-border pb-4 mb-4">
        <h1 className="text-2xl font-semibold">Announcements</h1>
        {tier === 'manager' ? (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium"
          >
            New announcement
          </button>
        ) : null}
      </header>

      {error ? <p className="text-sm text-destructive mb-3">{error}</p> : null}
      {loading && rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : null}

      <ul className="space-y-3">
        {rows.map((r) => (
          <li
            key={r.id}
            className={`border rounded-md p-4 ${
              r.pinned ? 'border-primary bg-primary/5' : 'border-border bg-card'
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-semibold">
                {r.pinned ? '📌 ' : ''}
                {r.title}
              </h2>
              <span className="text-xs text-muted-foreground">{formatShortDate(r.createdAt)}</span>
            </div>
            <p className="text-sm whitespace-pre-wrap">{r.body}</p>
            {r.mediaUrl ? (
              <a href={r.mediaUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={r.mediaUrl}
                  alt=""
                  loading="lazy"
                  className="mt-3 max-h-64 w-auto rounded-md border border-border"
                />
              </a>
            ) : null}
            <p className="text-xs text-muted-foreground mt-2">
              audience: {r.audience} ·{' '}
              {r.targetStoreIds.length === 0
                ? 'org-wide'
                : `${r.targetStoreIds.length} store${r.targetStoreIds.length === 1 ? '' : 's'}`}
            </p>
          </li>
        ))}
        {!loading && rows.length === 0 ? (
          <li className="py-6 text-center text-muted-foreground">No announcements yet.</li>
        ) : null}
      </ul>

      {createOpen ? (
        <CreateDialog
          scopedStores={scopedStores}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateDialog({
  scopedStores,
  onClose,
  onCreated,
}: {
  scopedStores: string[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<Audience>('all');
  const [pinned, setPinned] = useState(false);
  const [orgWide, setOrgWide] = useState(true);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { data: lookup } = useApi<{ stores: StoreLookup[] }>('/my/stores');

  function storeLabel(id: string): string {
    const s = lookup?.stores.find((x) => x.id === id);
    return s ? `${s.code} — ${s.name}` : id;
  }

  function toggleStore(id: string) {
    setSelectedStores((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function submit() {
    if (!title.trim() || !body.trim()) {
      setErr('title and body required');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      let mediaBlob: string | undefined;
      let mediaType: string | undefined;
      if (file) {
        const contentType = file.type || 'application/octet-stream';
        const sas = await apiFetch<{ uploadUrl: string; blobName: string }>('/uploads/sas', {
          method: 'POST',
          body: JSON.stringify({
            purpose: 'announcement-media',
            storeId: selectedStores[0] ?? scopedStores[0],
            filename: file.name,
            contentType,
          }),
        });
        const put = await fetch(sas.uploadUrl, {
          method: 'PUT',
          headers: { 'x-ms-blob-type': 'BlockBlob', 'content-type': contentType },
          body: file,
        });
        if (!put.ok) throw new Error(`media upload failed (${put.status})`);
        mediaBlob = sas.blobName;
        mediaType = contentType;
      }
      await apiFetch('/announcements', {
        method: 'POST',
        body: JSON.stringify({
          title,
          body,
          audience,
          pinned,
          expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
          storeIds: orgWide ? undefined : selectedStores,
          mediaBlob,
          mediaType,
        }),
      });
      onCreated();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'submit failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50 overflow-y-auto">
      <div className="bg-card rounded-xl p-6 w-full max-w-md space-y-3 my-8">
        <h3 className="text-lg font-semibold">New announcement</h3>
        <label className="block text-sm">
          Title
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full border border-border rounded-md px-2 py-1"
          />
        </label>
        <label className="block text-sm">
          Body
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="mt-1 w-full border border-border rounded-md px-2 py-1 min-h-[120px]"
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm">
            Audience
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value as Audience)}
              className="mt-1 w-full border border-border rounded-md px-2 py-1"
            >
              <option value="all">all tiers</option>
              <option value="managers">managers</option>
              <option value="baristas">baristas</option>
              <option value="shift_leads">shift leads</option>
            </select>
          </label>
          <label className="block text-sm">
            Expires
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="mt-1 w-full border border-border rounded-md px-2 py-1"
            />
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
          Pin to the top
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={orgWide} onChange={(e) => setOrgWide(e.target.checked)} />
          Org-wide (no specific stores)
        </label>
        {!orgWide ? (
          <div className="text-sm space-y-1 max-h-32 overflow-y-auto border border-border rounded-md p-2">
            {scopedStores.map((s) => (
              <label key={s} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedStores.includes(s)}
                  onChange={() => toggleStore(s)}
                />
                {storeLabel(s)}
              </label>
            ))}
          </div>
        ) : null}
        <label className="block text-sm">
          Attach image <span className="text-muted-foreground">(optional)</span>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="mt-1 w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-1.5 file:text-sm file:font-medium"
          />
          {file ? (
            <span className="mt-1 block text-xs text-muted-foreground">
              {file.name} ({Math.ceil(file.size / 1024)} KB)
            </span>
          ) : null}
        </label>
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1 text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy}
            className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60"
          >
            {busy ? 'Posting…' : 'Post'}
          </button>
        </div>
      </div>
    </div>
  );
}
